import { useState } from "react";

import { CheckCircle2, ChevronLeft, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import InputField from "@components/fields/input-field";
import SelectField from "@components/fields/select-field";
import Stepper from "@components/ui-components/stepper";
import useOnboardBrand from "./use-onboard-brand";
import {
    ONBOARD_STEPS
} from "./schema";
import { FREQUENCY_LABEL, PAYMENT_MODEL_OPTIONS, FREQUENCY_OPTIONS, PLATFORM_OPTIONS, PAYMENT_MODEL_LABEL, PLATFORM_LABEL } from "@src/utility/constants/panel/common";

const OnboardBrand = () => {

    const { methods, activeStep, isLastStep, goNext, goBack, loading } = useOnboardBrand();
    const { control, watch, formState: { errors } } = methods;
    const values = watch();

    const [smtpOpen, setSmtpOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploaded, setUploaded] = useState([]);

    const simulateUpload = (fileName) => {
        setUploadProgress(0);
        const timer = setInterval(() => {
            setUploadProgress((progress) => {
                if (progress >= 100) {
                    clearInterval(timer);
                    setUploaded((files) => [...files, fileName]);
                    return 0;
                }
                return progress + 20;
            });
        }, 150);
    };

    return (
        <div className="p-6">
            <Link to="/brands" className="inline-flex items-center gap-1 text-sm text-muted hover:text-gray-700">
                <ChevronLeft className="h-4 w-4" />
                Brands
            </Link>

            <h1 className="mt-2 text-2xl font-semibold tracking-tight">Onboard new brand</h1>
            <p className="mt-1 text-sm text-muted">9-field onboarding · Ops only</p>

            <div className="mt-4">
                <Stepper steps={ONBOARD_STEPS} activeStep={activeStep} />
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                {activeStep === 0 && (
                    <div className="grid gap-4 sm:grid-cols-2">
                        <InputField control={control} name="name" label="Brand name" placeholder="Acme Corp" errors={errors} />
                        <InputField control={control} name="country" label="Country" errors={errors} />
                        <div>
                            <SelectField control={control} name="payment_model" label="Payment model" options={PAYMENT_MODEL_OPTIONS} errors={errors} />
                            <p className="-mt-3 text-xs text-muted">Agency and Self-Pay models are coming soon.</p>
                        </div>
                        <SelectField control={control} name="debit_note_frequency" label="Debit note frequency" options={FREQUENCY_OPTIONS} errors={errors} />
                    </div>
                )}

                {activeStep === 1 && (
                    <div className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <SelectField control={control} name="platform_name" label="Platform" options={PLATFORM_OPTIONS} errors={errors} />
                            <div>
                                <InputField control={control} name="kyc_window" type="number" label="KYC window (days)" errors={errors} />
                                <p className="-mt-1 text-xs text-muted">Publishers must complete KYC within this many days of being linked.</p>
                            </div>
                        </div>

                        <div className="rounded-lg border border-gray-200">
                            <button
                                type="button"
                                onClick={() => setSmtpOpen(!smtpOpen)}
                                className="flex w-full items-center justify-between p-3 text-sm font-medium text-gray-900">
                                SMTP configuration
                                <span className="text-xs text-muted">{smtpOpen ? "Hide" : "Show"}</span>
                            </button>
                            {smtpOpen && (
                                <div className="grid gap-3 border-t border-gray-100 p-3 sm:grid-cols-2">
                                    <InputField control={control} name="smtp_host" label="Host" placeholder="smtp.brand.com" errors={errors} />
                                    <InputField control={control} name="smtp_port" label="Port" errors={errors} />
                                    <InputField control={control} name="smtp_user" label="Username" errors={errors} />
                                    <InputField control={control} name="smtp_pass" type="password" label="Password" errors={errors} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeStep === 2 && (
                    <div className="space-y-4">
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-gray-700">Brand KYC documents</label>
                            <div
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    simulateUpload(e.dataTransfer.files[0]?.name || "Certificate.pdf");
                                }}
                                className="mt-2 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 p-8 text-sm text-muted">
                                <Upload className="mb-2 h-6 w-6" />
                                <div>Drag & drop files here</div>
                                <button
                                    type="button"
                                    onClick={() => simulateUpload(`Incorporation-${uploaded.length + 1}.pdf`)}
                                    className="mt-3 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                                    or browse
                                </button>
                            </div>
                            {uploadProgress > 0 && (
                                <div className="mt-3 space-y-1">
                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
                                    </div>
                                    <p className="text-xs text-muted">Uploading… {uploadProgress}%</p>
                                </div>
                            )}
                            {uploaded.length > 0 && (
                                <ul className="mt-3 space-y-1 text-sm">
                                    {uploaded.map((file) => (
                                        <li key={file} className="flex items-center gap-2 text-success">
                                            <CheckCircle2 className="h-4 w-4" />
                                            {file}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div>
                            <label htmlFor="notes" className="mb-1.5 block text-sm font-medium text-gray-700">Notes</label>
                            <textarea
                                id="notes"
                                rows={4}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                        </div>
                    </div>
                )}

                {activeStep === 3 && (
                    <div className="space-y-3 text-sm">
                        <h3 className="font-semibold text-gray-900">Review</h3>
                        <dl className="grid gap-3 rounded-lg border border-gray-200 p-4 sm:grid-cols-2">
                            <div><dt className="text-xs text-muted">Brand</dt><dd className="font-medium text-gray-900">{values.name || "—"}</dd></div>
                            <div><dt className="text-xs text-muted">Country</dt><dd className="font-medium text-gray-900">{values.country || "—"}</dd></div>
                            <div><dt className="text-xs text-muted">Platform</dt><dd className="font-medium text-gray-900">{PLATFORM_LABEL[values.platform_name] || values.platform_name}</dd></div>
                            <div><dt className="text-xs text-muted">Model</dt><dd className="font-medium text-gray-900">{PAYMENT_MODEL_LABEL[values.payment_model] || values.payment_model}</dd></div>
                            <div><dt className="text-xs text-muted">KYC window</dt><dd className="font-medium text-gray-900">{values.kyc_window} days</dd></div>
                            <div><dt className="text-xs text-muted">Frequency</dt><dd className="font-medium text-gray-900">{FREQUENCY_LABEL[values.debit_note_frequency] || values.debit_note_frequency}</dd></div>
                            <div><dt className="text-xs text-muted">SMTP</dt><dd className="font-medium text-gray-900">{values.smtp_host ? "Configured" : "Not set"}</dd></div>
                        </dl>
                    </div>
                )}

                <div className="mt-6 flex justify-between border-t border-gray-100 pt-5">
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={activeStep === 0}
                        className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={goNext}
                        disabled={loading}
                        className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">
                        {isLastStep ? (loading ? "Creating…" : "Create brand") : "Next"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OnboardBrand;
